import React, { useEffect, useState } from "react";
import { ArrowDownRight, Github, Linkedin, Twitter } from "lucide-react";
import { profile } from "../mock";

const Hero = () => {
  const [time, setTime] = useState("");

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setTime(
        now.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }),
      );
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden bg-[#0a0a0a] text-[#f4f1ea] grain"
    >
      {/* Decorative grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(#f4f1ea 1px, transparent 1px), linear-gradient(90deg, #f4f1ea 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at center, black 40%, transparent 75%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 40%, transparent 75%)",
        }}
      />

      {/* Lime glow blob */}
      <div
        className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(160, 29, 63,0.08) 0%, transparent 70%)",
        }}
      />

      <div className="container-x relative">
        {/* Top metadata row */}
        <div className="flex items-center justify-between text-xs font-mono uppercase tracking-widest text-[#f4f1ea]/55 mb-12 md:mb-16">
          <span className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#a01d3f] opacity-60"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#a01d3f]"></span>
            </span>
            Available for new work
          </span>
          <span className="hidden sm:block">{profile.location}</span>
          <span>{time} · PT</span>
        </div>

        <div className="grid grid-cols-12 gap-y-10 md:gap-x-8 items-end">
          <div className="col-span-12 md:col-span-9 reveal-up">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#a01d3f] mb-6">
              <span className="text-[#f4f1ea]/40">$</span> portfolio --year 2026
            </p>
            <h1 className="font-display text-[12vw] md:text-[8.5vw] lg:text-[7.2rem] font-semibold leading-[0.95] tracking-tight">
              {profile.name.split(" ")[0]}
              <br />
              <span className="italic font-light text-white">
                {profile.name.split(" ")[1]}
              </span>
              <span className="text-[#a01d3f] animate-blink">_</span>
            </h1>
          </div>

          <div
            className="col-span-12 md:col-span-3 reveal-up"
            style={{ animationDelay: "120ms" }}
          >
            <div className="text-sm leading-relaxed text-[#f4f1ea]/70 md:text-right">
              <p className="mb-2 font-medium text-[#f4f1ea]">{profile.role}</p>
              <p>{profile.tagline}</p>
            </div>
          </div>
        </div>

        <div className="mt-16 md:mt-24 grid grid-cols-12 gap-y-10 md:gap-x-8 items-end border-t border-[#f4f1ea]/15 pt-10">
          <div
            className="col-span-12 md:col-span-7 flex flex-wrap gap-4 reveal-up"
            style={{ animationDelay: "240ms" }}
          >
            <button
              onClick={() => scrollTo("contact")}
              className="btn-lime inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium"
            >
              Start a conversation
              <ArrowDownRight size={16} />
            </button>
            <button
              onClick={() => scrollTo("about")}
              className="btn-outline-light inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium"
            >
              About me
            </button>
          </div>

          <div
            className="col-span-12 md:col-span-5 flex md:justify-end gap-6 reveal-up"
            style={{ animationDelay: "320ms" }}
          >
            <a
              href={profile.socials.github}
              target="_blank"
              rel="noreferrer"
              aria-label="GitHub"
              className="text-[#f4f1ea]/55 hover:text-[#a01d3f] transition-colors"
            >
              <Github size={20} />
            </a>
            <a
              href={profile.socials.linkedin}
              target="_blank"
              rel="noreferrer"
              aria-label="LinkedIn"
              className="text-[#f4f1ea]/55 hover:text-[#a01d3f] transition-colors"
            >
              <Linkedin size={20} />
            </a>
          </div>
        </div>

        {/* Stat strip */}
        <div
          className="mt-20 md:mt-28 grid grid-cols-3 gap-6 md:gap-12 reveal-up"
          style={{ animationDelay: "420ms" }}
        >
          {[
            {
              value: `${profile.yearsExperience}+`,
              label: "Years building software",
            },
            { value: profile.rolesShipped, label: "Engineering roles shipped" },
            { value: profile.perfGains, label: "Average performance wins" },
          ].map((s) => (
            <div key={s.label} className="border-t border-[#f4f1ea]/15 pt-5">
              <div className="font-display text-3xl md:text-5xl font-semibold tracking-tight">
                {s.value}
              </div>
              <div className="mt-2 text-xs md:text-sm text-[#f4f1ea]/55 max-w-[180px]">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
